import { useSignal } from "@preact/signals-react";
import { ErrorField } from "../../../../types/ErrorField";
import TextEdit from "../../../molecules/TextEdit";
import SelectTextField, { OptionProps } from "../../../molecules/SelectTextField";
import { getErrorMessage } from "../../../../utils/formErrorUtils";
import { useEffect } from "react";
import MaskEdit from "../../../organisms/MaskEdit";
import { City } from "../../../../hooks/useCities";
import streetTypesData from "../../../../resources/street_types.json";



interface Props {
    form: any;
    errorFields: ErrorField[];
    changeForm: (field: string, value: string) => void;
    provinces: any[];
    cities: any[],
    changeProvince: (province: string) => void;
    editForm?: boolean;
}


const IndividualFormBasicData: React.FC<Props> = ({ form, errorFields, changeForm, provinces, cities, changeProvince, editForm = false }) => {
    const provincesOpt = useSignal<OptionProps[]>([]);
    const citiesOpt = useSignal<OptionProps[]>([]);
    const isLoadingCities = useSignal(false);
    const streetTypeOptions = useSignal<OptionProps[]>([]);
    const currentIbge = useSignal<string>(""); // ✅ Armazenar IBGE localmente

    // ✅ Função para autofill por CEP (igual ao LegalPersonFormTab1)
    const handleZipcodeChange = async (value: string) => {
        // Remove formatação e mantém apenas números
        value = value.replace(/[^0-9]/g, "");

        if (value.length === 0) {
            // Limpar campos quando CEP é removido
            changeForm("activeAddress.street_type", "");
            changeForm("activeAddress.street", "");
            changeForm("activeAddress.district", "");
            changeForm("activeAddress.complement", "");
            changeForm("activeAddress.province_initials", "");
            return;
        }

        if (value.length === 8) {
            try {
                const response = await fetch(`https://viacep.com.br/ws/${value}/json/`);
                const data = await response.json();

                if (data.erro) {
                    console.warn("CEP não encontrado:", value);
                    return;
                }

                // ✅ Autofill dos campos de endereço
                const logradouro = data.logradouro || "";
                const streetTypeName = logradouro.split(" ")[0] || "";
                
                // ✅ Buscar streetType correspondente no resource
                const matchingStreetType = streetTypesData.streetTypes.find(st => 
                    st.name === streetTypeName || st.abbreviations.includes(streetTypeName)
                );
                
                // ✅ Remover o tipo de logradouro do campo street
                const streetWithoutType = matchingStreetType 
                    ? logradouro.replace(streetTypeName, "").trim()
                    : logradouro;
                
                changeForm("activeAddress.street_type", matchingStreetType?.name || streetTypeName);
                changeForm("activeAddress.street", streetWithoutType);
                changeForm("activeAddress.district", data.bairro || ""); // ✅ Campo correto
                changeForm("activeAddress.complement", data.complemento || "");
                
                // ✅ Atualizar estado e carregar cidades
                if (data.uf) {
                    changeForm("activeAddress.province_initials", data.uf);
                    changeProvince(data.uf); // Carregar cidades do estado
                }

                // ✅ Setar código IBGE e aguardar cidades
                if (data.ibge) {
                    currentIbge.value = data.ibge;
                    
                    // Aguardar cidades carregarem e selecionar por IBGE
                    setTimeout(() => {
                        const cityMatch = citiesOpt.value.find((city: any) => city.ibgeCode === data.ibge);
                        if (cityMatch) {
                            changeForm("activeAddress.city_id", cityMatch.value);
                            console.log("✅ Cidade selecionada automaticamente:", cityMatch.label);
                        }
                    }, 500); // ✅ Tempo maior para aguardar carregamento
                }

                console.log("✅ Autofill por CEP realizado:", data);
            } catch (error) {
                console.error("❌ Erro ao buscar CEP:", error);
            }
        }
    };

    useEffect(() => {
        // ✅ Popular streetTypeOptions com resource JSON
        streetTypeOptions.value = streetTypesData.streetTypes.map(st => ({
            value: st.name,
            label: st.name
        }));
    }, []);

    useEffect(() => {
        isLoadingCities.value = true;
        if (cities && cities.length > 0) {
            citiesOpt.value = cities.map((city: any) => ({
                label: city.name,
                value: city.id,
                ibgeCode: city.ibgeCode // ✅ Incluir IBGE code para matching
            }));
            if (currentIbge.value) {
                setTimeout(() => {
                    const cityMatch = citiesOpt.value.find((city: any) => city.ibgeCode === currentIbge.value);
                    if (cityMatch) {
                        changeForm("cityId", cityMatch.value);
                        console.log("✅ Cidade selecionada automaticamente:", cityMatch.label);
                    }
                }, 300);
            }
        } else {
            citiesOpt.value = []; // ✅ Garantir array vazio se cities não existir
        }
        isLoadingCities.value = false;
    }, [cities]);
    useEffect(() => {
        if (provinces && provinces.length > 0) {
            provincesOpt.value = provinces.map((province: any) => ({
                label: province.name,
                value: province.initials,
            }));
        } else {
            provincesOpt.value = []; // ✅ Garantir array vazio se provinces não existir
        }

    }, [provinces]);

    return (
        <div className="p-4 m-4">
            {/* Primeira linha: CPF + Nome */}
            <div className="grid grid-cols-12 gap-8">
                <MaskEdit
                    id="socialId"
                    label="CPF"
                    mask="___.___.___-__"
                    replacement={{ '_': /\d/ }}
                    className="col-span-3"
                    value={form.socialId}
                    disabled={true}
                    onChange={(e) => changeForm("socialId", e.target.value)}
                    errorMessage={getErrorMessage(errorFields, "social_id")}
                />
                <TextEdit
                    id="name"
                    label="Nome"
                    className="col-span-9"
                    value={form.name}
                    onChange={(e) => changeForm("name", e.target.value.toUpperCase())}
                    errorMessage={getErrorMessage(errorFields, "name")}
                />
            </div>

            {/* Segunda linha: Tipo ID + Número + UF emissor (reduzida) + Órgão emissor + Email */}
            <div className="grid grid-cols-12 gap-8 mt-6">
                <SelectTextField
                    id="idCardType"
                    name='idCardType'
                    label=""
                    disabled={form.blockEdit.blockIdCard}
                    value={form.idCardType}
                    items={[{ "value": "RG", "label": "RG" }, { "value": "RNE", "label": "RNE" }]}
                    className="col-span-1"
                    onChange={(e) => changeForm("idCardType", e.target.value)}
                    errorMessage={getErrorMessage(errorFields, "idCardType")}
                />

                <TextEdit
                    id="idCard"
                    label="Número RG/RNE"
                    className="col-span-2"
                    value={form.idCard}
                    disabled={form.blockEdit.blockIdCard}
                    onChange={(e) => changeForm("idCard", e.target.value.toUpperCase())}
                    errorMessage={getErrorMessage(errorFields, "idCardNumber")}
                />

                <SelectTextField
                    id="idCardProvince"
                    name='idCardProvince'
                    label="UF emissor"
                    value={form.idCardType === "RG" ? form.idCardProvince : ""}
                    items={provincesOpt.value}
                    className="col-span-2"
                    disabled={form.idCardType === "RNE" || form.blockEdit.blockIdCard}
                    onChange={(e) => {
                        changeForm("idCardProvince", e.target.value);
                    }}
                    errorMessage={getErrorMessage(errorFields, "idCardProvince")}
                />

                <TextEdit
                    id="issuingAuthority"
                    label="Orgão emissor"
                    className="col-span-2"
                    value={form.idCardType === "RG" ? form.issuingAuthority : ""}
                    disabled={form.idCardType === "RNE" || form.blockEdit.blockIdCard}
                    onChange={(e) => changeForm("issuingAuthority", e.target.value.toUpperCase())}
                    errorMessage={getErrorMessage(errorFields, "issuingAuthority")}
                />

                <TextEdit
                    id="email"
                    label="Email"
                    className="col-span-5"
                    value={form.email}
                    onChange={(e) => changeForm("email", e.target.value.toUpperCase())}
                    errorMessage={getErrorMessage(errorFields, "email")}
                />
            </div>

            {/* Terceira linha: Origem celular + Celular + Fixo */}
            <div className="grid grid-cols-12 gap-8 mt-6">
                <SelectTextField
                    id="isNationalNumber"
                    name='isNationalNumber'
                    label="Origem celular"
                    value={form.isNationalNumber}
                    items={[{ "value": "true", "label": "Brasil" }, { "value": "false", "label": "Estrangeiro" }]}
                    className="col-span-4"
                    onChange={(e) => changeForm("isNationalNumber", e.target.value)}
                    errorMessage={getErrorMessage(errorFields, "isNationalNumber")}
                />
                <MaskEdit
                    id="mobile"
                    label="Celular"
                    mask={form.isNationalNumber == "true" ? "(__)_____-____" : "___________________________"}
                    replacement={{ '_': /\d/ }}
                    className="col-span-4"
                    value={form.mobile}
                    onChange={(e) => changeForm("mobile", e.target.value)}
                    errorMessage={getErrorMessage(errorFields, "mobile")}
                />
                <MaskEdit
                    id="phone"
                    label="Fixo"
                    mask="(__)____-____"
                    replacement={{ '_': /\d/ }}
                    className="col-span-4"
                    value={form.phone}
                    onChange={(e) => changeForm("phone", e.target.value)}
                    errorMessage={getErrorMessage(errorFields, "phone")}
                />
            </div>

            {/* Quarta linha: CEP + Tipo (50% maior) + Logradouro (33% maior) + Número (50% menor) */}
            <div className="grid grid-cols-12 gap-8 mt-6">
                <MaskEdit
                    id="zipcode"
                    label="CEP"
                    mask="_____-___"
                    replacement={{ '_': /\d/ }}
                    className="col-span-2"
                    value={form.activeAddress?.zipcode || ''}
                    onChange={(e) => {
                        const value = e.target.value.toUpperCase();
                        changeForm("activeAddress.zipcode", value);
                        handleZipcodeChange(value);
                    }}
                    errorMessage={getErrorMessage(errorFields, "zipcode")}
                />

                <SelectTextField
                    id="streetType"
                    name='streetType'
                    label="Tipo"
                    value={form.activeAddress?.street_type || ''}
                    className="col-span-2"
                    onChange={(e) => changeForm("activeAddress.street_type", e.target.value)}
                    items={streetTypeOptions.value}
                    errorMessage={getErrorMessage(errorFields, "streetType")}
                />

                <TextEdit
                    id="street"
                    name='street'
                    label="Logradouro"
                    value={form.activeAddress?.street || ''}
                    className="col-span-4"
                    onChange={(e) => changeForm("activeAddress.street", e.target.value)}
                    errorMessage={getErrorMessage(errorFields, "street")}
                />

                <TextEdit
                    id="streetNumber"
                    name='streetNumber'
                    label="Numero"
                    value={form.activeAddress?.street_number || ''}
                    className="col-span-2"
                    onChange={(e) => changeForm("activeAddress.street_number", e.target.value)}
                    errorMessage={getErrorMessage(errorFields, "streetNumber")}
                />

                <TextEdit
                    id="complement"
                    label="Complemento"
                    className="col-span-2"
                    value={form.activeAddress?.complement || ''}
                    onChange={(e) => changeForm("activeAddress.complement", e.target.value)}
                    errorMessage={getErrorMessage(errorFields, "complement")}
                />
            </div>

            {/* Quinta linha: Bairro + UF + Cidade */}
            <div className="grid grid-cols-12 gap-8 mt-6">
                <TextEdit
                    id="district"
                    label="Bairro"
                    className="col-span-4"
                    value={form.activeAddress?.district || ''}
                    onChange={(e) => changeForm("activeAddress.district", e.target.value.toUpperCase())}
                    errorMessage={getErrorMessage(errorFields, "district")}
                />

                <SelectTextField
                    id="provinceId"
                    name='provinceId'
                    label="UF"
                    value={form.activeAddress?.province_initials || ''}
                    items={provincesOpt.value}
                    className="col-span-4"
                    onChange={(e) => {
                        changeForm("activeAddress.province_initials", e.target.value);
                        changeProvince(e.target.value);
                    }}
                    errorMessage={getErrorMessage(errorFields, "province")}
                />
                <SelectTextField
                    id="city"
                    label="Cidade"
                    className="col-span-4"
                    value={form.activeAddress?.city_id || ''}
                    items={citiesOpt.value}
                    isLoading={isLoadingCities.value}
                    onChange={(e) => changeForm("activeAddress.city_id", e.target.value)}
                    errorMessage={getErrorMessage(errorFields, "cityId")}
                    disabled={isLoadingCities.value}
                />
            </div>

        </div >
    );
};
export default IndividualFormBasicData;
